import { Bundle, Resource } from "@medplum/fhirtypes";
import { ConsolidatedQuery, MetriportMedicalApi } from "@metriport/api-sdk";
import { errorToString, MetriportError } from "@metriport/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { getConsolidatedWebhook } from "../../../api/consolidated-bundle";
import { capture } from "../../../shared/capture";
import { swallowTimeoutOrNetworkError } from "../../error/utils";
import { usePatientConsolidatedContext } from "./consolidated-context";
import {
  MappedConsolidatedResources,
  PatientConsolidatedStateActionType,
} from "./consolidated-context/reducer";
import { useBundleGroupingWorker } from "./useBundleGroupingWorker";
import { useMetriportApi } from "./useMetriportApi";
import useMetriportToast from "./useMetriportToast";

type UseGetConsolidated = {
  consolidated: Bundle<Resource> | undefined;
  consolidatedResources: MappedConsolidatedResources | undefined;
  isQueryingConsolidated: boolean;
  onQueryPatientConsolidated: () => Promise<void>;
  onInvalidateConsolidated: () => void;
  isConsolidatedFailed: boolean;
};

const POLLING_INTERVAL = dayjs.duration(2, "second").asMilliseconds();
const EXPLAIN_TOAST_DURATION = dayjs.duration(5, "second").asMilliseconds();
const POLL_ERROR_MESSAGE = "Failed to get patient consolidated data";
const START_PROCESSING_MESSAGE =
  "Consolidated data is being processed. This may take some time depending on how much data is available.";
const START_CONSOLIDATED_QUERY_ERROR_MESSAGE = "Failed to start patient consolidated query";

const CONSOLIDATED_QUERY_KEY = "consolidated";
const CONSOLIDATED_BY_RESOURCE_QUERY_KEY = "consolidated-by-resource";

const consolidatedKeys = {
  consolidated: (patientId: string, requestId: string) =>
    [CONSOLIDATED_QUERY_KEY, patientId, requestId] as const,
  consolidatedResources: (patientId: string, requestId: string) =>
    [CONSOLIDATED_BY_RESOURCE_QUERY_KEY, patientId, requestId] as const,
};

export function useGetConsolidatedRQ({ patientId }: { patientId: string }): UseGetConsolidated {
  const [isPollLoading, setIsPollLoading] = useState(false);
  const [isConsolidatedFailed, setIsConsolidatedFailed] = useState(false);

  const metriportApi = useMetriportApi();
  const toast = useMetriportToast();
  const processBundle = useBundleGroupingWorker();
  const invalidateConsolidated = useInvalidateConsolidated();
  const { patientConsolidatedDispatch, patientConsolidatedState } = usePatientConsolidatedContext();

  const lastConsolidatedRequestId =
    patientConsolidatedState[patientId]?.lastConsolidatedRequestId || "";
  const isPollEnabled = !!patientId && !!lastConsolidatedRequestId;

  // 1. Poll for consolidated bundle status and download bundle when ready
  const pollConsolidatedBundleQuery = useQuery({
    queryKey: consolidatedKeys.consolidated(patientId, lastConsolidatedRequestId),
    queryFn: async () => {
      setIsPollLoading(true);
      return await getConsolidatedBundle(patientId, lastConsolidatedRequestId, metriportApi);
    },
    enabled: isPollEnabled,
    refetchIntervalInBackground: true, // Continue polling in background
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000,
    refetchInterval: query => {
      if (!isPollEnabled) return false;
      const data = query.state.data;
      if (data?.status === "completed" || data?.status === "failed") {
        setIsPollLoading(false);
        return false;
      }
      return POLLING_INTERVAL;
    },
    meta: {
      errorMessage: POLL_ERROR_MESSAGE,
      toastDuration: EXPLAIN_TOAST_DURATION,
      toast: toast,
    },
  });

  const pollError = pollConsolidatedBundleQuery.error;
  const consolidated = pollConsolidatedBundleQuery.data?.bundle;

  useEffect(() => {
    if (!pollError) return;
    setLastConsolidatedRequestId("");
    setIsPollLoading(false);
    setIsConsolidatedFailed(true);
    // TODO swallow if network error
    // /  const { handled } = swallowTimeoutOrNetworkError(error);
    capture.error(POLL_ERROR_MESSAGE, {
      extra: {
        patient: patientId,
        context: `patient.get.consolidated-status`,
        error: errorToString(pollError),
      },
    });
  }, [pollError]);

  // 2. Heavy processing with caching and web worker
  const processedDataQuery = useQuery({
    queryKey: consolidatedKeys.consolidatedResources(patientId, lastConsolidatedRequestId),
    queryFn: () => (consolidated ? processBundle(consolidated) : undefined),
    enabled: !!consolidated,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const consolidatedResources = processedDataQuery.data;

  const startConsolidatedQuery = useMutation({
    mutationFn: async () => {
      const query = await metriportApi.startConsolidatedQuery(
        patientId,
        undefined,
        undefined,
        undefined,
        "json",
        true
      );
      return query.requestId;
    },
    onSuccess: newRequestId => {
      setLastConsolidatedRequestId(newRequestId);
      toast.info({ title: START_PROCESSING_MESSAGE, duration: EXPLAIN_TOAST_DURATION });
    },
    onError: error => {
      setLastConsolidatedRequestId("");
      toast.error({ title: START_CONSOLIDATED_QUERY_ERROR_MESSAGE });
      capture.error(START_CONSOLIDATED_QUERY_ERROR_MESSAGE, {
        extra: {
          patient: patientId,
          context: `patient.get.consolidated`,
          error: errorToString(error),
        },
      });
    },
  });

  function setLastConsolidatedRequestId(requestId: string) {
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setPatientsLastConsolidatedRequestId,
      patientId,
      lastConsolidatedRequestId: requestId,
    });
  }

  const onQueryPatientConsolidated = async () => {
    await startConsolidatedQuery.mutateAsync();
  };

  const isQueryingConsolidated = startConsolidatedQuery.isPending || isPollLoading;

  function onInvalidateConsolidated() {
    invalidateConsolidated(patientId, lastConsolidatedRequestId);
  }

  return {
    consolidated,
    consolidatedResources,
    isQueryingConsolidated,
    onQueryPatientConsolidated,
    onInvalidateConsolidated,
    isConsolidatedFailed,
  };
}

export function useInvalidateConsolidated() {
  const queryClient = useQueryClient();
  return (patientId: string, requestId: string) => {
    queryClient.invalidateQueries({
      queryKey: consolidatedKeys.consolidated(patientId, requestId),
    });
    queryClient.invalidateQueries({
      queryKey: consolidatedKeys.consolidatedResources(patientId, requestId),
    });
  };
}

async function getConsolidatedBundle(
  patientId: string,
  requestId: string,
  metriportApi: MetriportMedicalApi
): Promise<{ status: string; bundle?: Bundle<Resource> }> {
  try {
    const { queries } = await metriportApi.getConsolidatedQueryStatus(patientId);
    const query = queries?.find((q: ConsolidatedQuery) => q.requestId === requestId);

    if (!query) {
      throw new MetriportError(`Consolidated query not found`, undefined, { requestId, patientId });
    }

    if (query.status !== "completed") return { status: query.status };

    const webhook = await getConsolidatedWebhook(patientId, requestId);
    const { fileUrl, gzipUrl } = webhook;

    if (gzipUrl) {
      const gzipResponse = await axios.get(gzipUrl);
      return { status: "completed", bundle: gzipResponse.data as Bundle<Resource> };
    }

    if (fileUrl) {
      const response = await axios.get(fileUrl);
      return { status: "completed", bundle: response.data as Bundle<Resource> };
    }

    throw new MetriportError(`No file or gzip URL in webhook response`, undefined, {
      requestId,
      patientId,
    });
  } catch (error) {
    const { handled } = swallowTimeoutOrNetworkError(error);
    if (handled) return { status: "processing" };
    throw new MetriportError(`Failed to get consolidated bundle`, error, {
      requestId,
      patientId,
    });
  }
}
